import {memo, forwardRef} from "react";
import {Icon} from "@chakra-ui/react";
import {ChevronDownIcon, LockClosedIcon, LockOpenIcon} from "@heroicons/react/20/solid";
import {Button, type ButtonProps} from "../../../../components/ui/button";
import {MenuContent, MenuItem, MenuRoot, MenuTrigger} from "../../../../components/ui/menu";
import T from "platformed-components/i18n/T";
import {ButtonContentSizer} from "./ButtonContentSizer";

const LockButton = forwardRef<HTMLButtonElement, {locked: boolean; withChevron?: boolean} & ButtonProps>(
  function LockButton({locked, withChevron, ...props}, ref) {
    const variantProps: ButtonProps = locked
      ? {variant: "solid", bg: "yellow.500", color: "white", _hover: {bg: "yellow.600"}}
      : {variant: "outline", colorPalette: "gray", bg: "white"};
    // Reserve room for both states so the button never resizes when toggling locked/unlocked.
    const variants = [
      <>
        <Icon asChild boxSize="3.5">
          <LockOpenIcon />
        </Icon>
        <T keyName="c.Lock" />
      </>,
      <>
        <Icon asChild boxSize="3.5">
          <LockClosedIcon />
        </Icon>
        <T keyName="c.Locked" />
        {withChevron && (
          <Icon asChild boxSize="3.5">
            <ChevronDownIcon />
          </Icon>
        )}
      </>,
    ];
    return (
      <Button ref={ref} size="xs" {...variantProps} {...props}>
        <ButtonContentSizer active={locked ? 1 : 0} variants={variants} />
      </Button>
    );
  },
);

/**
 * Lock / unlock control for an engagement entity. Presentational and
 * entity-agnostic: the caller owns the lock semantics via `onToggle`. Pass
 * `disabled` to render the current state non-interactively (e.g. an entity that
 * is locked as derived state, or before its lock API exists).
 *
 * Locking always cascades to descendants, so it is a single button. Unlocking a
 * page that has sub-pages becomes a menu offering to unlock this page alone or
 * the whole subtree.
 */
export const LockToggle = memo(
  ({
    locked,
    hasChildren,
    onToggle,
    loading,
    disabled,
  }: {
    locked: boolean;
    hasChildren: boolean;
    onToggle: (locked: boolean, includeChildren: boolean) => void;
    loading?: boolean;
    disabled?: boolean;
  }) => {
    if (locked && hasChildren) {
      return (
        <MenuRoot>
          <MenuTrigger asChild>
            <LockButton locked={locked} withChevron loading={loading} disabled={disabled} />
          </MenuTrigger>
          <MenuContent>
            <MenuItem value="unlock" onClick={() => onToggle(false, false)}>
              <T keyName="c.Unlock_this_page" />
            </MenuItem>
            <MenuItem value="unlock-children" onClick={() => onToggle(false, true)}>
              <T keyName="c.Unlock_with_all_subpages" />
            </MenuItem>
          </MenuContent>
        </MenuRoot>
      );
    }

    return (
      <LockButton
        locked={locked}
        withChevron={hasChildren}
        loading={loading}
        disabled={disabled}
        onClick={() => onToggle(!locked, false)}
      />
    );
  },
);
